import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, FileXls, CheckCircle, X, DownloadSimple, ListChecks } from "@phosphor-icons/react";

/**
 * TrackerUpdateModal — lighter-weight sibling of CaseImportModal for tier-3+ ops staff.
 *
 * Only updates status / stage / latest update / next action / priority / assignee on
 * EXISTING cases, identified by Reference no. Never inserts new cases.
 */
export default function TrackerUpdateModal({ onClose, onApplied }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [executed, setExecuted] = useState(null);

  const dlTemplate = async (fmt) => {
    try {
      const res = await api.get(`/cases/tracker-updates/template.${fmt}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = `SPAN_Tracker_Updates_Template.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      setErr(`Template download failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.(xlsx|xlsm|csv)$/i.test(f.name)) {
      setErr("File must be .xlsx, .xlsm or .csv"); return;
    }
    setErr(null); setFile(f); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/cases/tracker-updates/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Preview failed");
      setFile(null);
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!file || !preview) return;
    if (!window.confirm(
      `Apply tracker updates to ${preview.summary.to_update} case(s)?\n\n` +
      `• ${preview.summary.to_update} updates will be applied\n` +
      `• ${preview.summary.no_change} unchanged rows skipped\n` +
      `• ${preview.summary.not_found} references not in the system (will be reported)\n` +
      `• ${preview.summary.invalid} invalid rows ignored\n\n` +
      `Each updated case will also receive an Activity entry tagged 'tracker_bulk_update'.`
    )) return;

    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirmation", "CONFIRM-TRACKER-UPDATES");
      const { data } = await api.post("/cases/tracker-updates/execute", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExecuted(data);
      if (typeof onApplied === "function") onApplied(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Apply failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4" data-testid="tracker-update-modal">
      <div className="bg-[var(--surface,#fff)] rounded-lg shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks size={20} weight="duotone" className="text-teal-600" />
            <div>
              <h2 className="font-bold text-[var(--text-primary)]">Bulk update tracker</h2>
              <div className="text-[11px] text-[var(--text-muted)]">Status / stage / latest update only · tier 3+ · never inserts new cases</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="tracker-update-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          <section>
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Step 1 — Download a blank template</div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => dlTemplate("xlsx")} data-testid="dl-tracker-template-xlsx"
                className="span-btn span-btn-ghost h-8 text-[12px] inline-flex items-center gap-1.5">
                <DownloadSimple size={13} /> Excel template
              </button>
              <button onClick={() => dlTemplate("csv")} data-testid="dl-tracker-template-csv"
                className="span-btn span-btn-ghost h-8 text-[12px] inline-flex items-center gap-1.5">
                <DownloadSimple size={13} /> CSV template
              </button>
              <span className="text-[11px] text-[var(--text-muted)] self-center">
                Only 8 columns — Reference no., Stage, Status, Priority, Latest update + date, Next action, Assignee.
              </span>
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Step 2 — Upload your filled file</div>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm,.csv" onChange={onFileChange}
                   data-testid="tracker-update-file-input" className="hidden" />
            <button onClick={() => fileInputRef.current?.click()} disabled={busy}
                    data-testid="tracker-update-pick-file"
                    className="span-btn h-9 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50">
              <FileXls size={14} /> {file ? `Re-pick (${file.name})` : "Pick Excel / CSV file"}
            </button>
          </section>

          {busy && !preview && <div className="text-[12px] text-[var(--text-muted)]">Parsing file…</div>}

          {preview && (
            <section data-testid="tracker-update-preview">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Step 3 — Review the changes</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <Tile label="To update" value={preview.summary.to_update} tone="success" testid="tile-update" />
                <Tile label="No change" value={preview.summary.no_change} tone="muted" testid="tile-nochange" />
                <Tile label="Not found" value={preview.summary.not_found} tone="warn" testid="tile-notfound" />
                <Tile label="Invalid" value={preview.summary.invalid} tone="warn" testid="tile-invalid" />
              </div>

              {preview.samples?.to_update?.length > 0 && (
                <details open className="mb-2 text-[12px]" data-testid="sample-to-update">
                  <summary className="cursor-pointer font-semibold text-[var(--brand-primary)]">
                    {preview.samples.to_update.length} of {preview.summary.to_update} change(s) shown
                  </summary>
                  <div className="mt-1 border border-[var(--border)] rounded divide-y divide-[var(--border)] max-h-80 overflow-auto">
                    {preview.samples.to_update.map((r, i) => (
                      <div key={i} className="p-2.5 text-[11px]">
                        <div className="font-bold text-[12px]">
                          <span className="font-mono">{r.ref}</span>
                          <span className="text-[var(--text-muted)] font-normal ml-2">{r.client}</span>
                        </div>
                        <ul className="pl-4 list-disc mt-1 space-y-0.5">
                          {Object.entries(r.diff).map(([k, v]) => (
                            <li key={k}>
                              <span className="font-mono text-[10px] bg-[var(--surface-2)] px-1 py-0.5 rounded">{k}</span>
                              {" "}<span className="text-red-700 line-through">{String(v.was || "—").slice(0, 60)}</span>
                              {" → "}<span className="text-emerald-700 font-semibold">{String(v.becomes || "—").slice(0, 60)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {preview.samples?.not_found?.length > 0 && (
                <details className="mb-2 text-[12px]" data-testid="sample-not-found">
                  <summary className="cursor-pointer font-semibold text-amber-700">
                    {preview.samples.not_found.length} reference(s) not found in the system
                  </summary>
                  <ul className="mt-1 pl-4 list-disc">
                    {preview.samples.not_found.map((r, i) => (
                      <li key={i}>Row {r.row}: <span className="font-mono">{r.ref}</span> — no matching case</li>
                    ))}
                  </ul>
                </details>
              )}

              {preview.samples?.invalid?.length > 0 && (
                <details className="mb-2 text-[12px]" data-testid="sample-invalid">
                  <summary className="cursor-pointer font-semibold text-amber-700">
                    {preview.samples.invalid.length} invalid row(s)
                  </summary>
                  <ul className="mt-1 pl-4 list-disc">
                    {preview.samples.invalid.map((r, i) => (
                      <li key={i}>Row {r.row}: <span className="text-amber-700">{r.reason}</span></li>
                    ))}
                  </ul>
                </details>
              )}
            </section>
          )}

          {executed && (
            <div className="border border-emerald-200 bg-emerald-50 text-emerald-900 rounded p-3 text-[12px]" data-testid="tracker-update-result">
              <div className="font-bold flex items-center gap-1.5 mb-1"><CheckCircle size={14} /> Tracker updates applied</div>
              <ul className="pl-4 list-disc">
                <li>Updated: <strong>{executed.applied.updated}</strong></li>
                <li>No change: <strong>{executed.applied.no_change}</strong></li>
                <li>Not found: <strong>{executed.applied.not_found}</strong></li>
                <li>Invalid: <strong>{executed.applied.invalid}</strong></li>
              </ul>
              {executed.not_found?.length > 0 && (
                <div className="mt-2 text-[11px]">
                  <strong>Not-found references:</strong> {executed.not_found.join(", ")}
                </div>
              )}
            </div>
          )}

          {err && (
            <div className="text-[12px] text-red-800 bg-red-50 border border-red-200 px-3 py-2 rounded" data-testid="tracker-update-error">
              {String(err)}
            </div>
          )}
        </div>

        <div className="border-t border-[var(--border)] px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="span-btn span-btn-ghost">Close</button>
          {preview && !executed && preview.summary.to_update > 0 && (
            <button onClick={apply} disabled={busy} data-testid="tracker-update-apply"
                    className="span-btn bg-teal-600 hover:bg-teal-700">
              {busy ? "Applying…" : `Apply ${preview.summary.to_update} update(s)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, testid, tone }) {
  const toneClass = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
    warn:    "border-amber-200 bg-amber-50 text-amber-900",
    muted:   "border-[var(--border)] bg-[var(--surface-2)] text-[var(--text-primary)]",
  }[tone] || "";
  return (
    <div className={`border rounded p-2.5 ${toneClass}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums leading-tight">{value}</div>
    </div>
  );
}
